<template>
  <div class="platform-access-page">
    <div class="page-header">
      <div class="page-header__copy">
        <p class="eyebrow">PREIKA PROVIDER CONTROL</p>
        <h1>契約企業管理</h1>
        <p>
          PREIKA運営管理者が、契約企業の開通、契約プラン、利用人数上限、利用可能機能、
          最初の契約企業管理者への招待を管理します。契約企業側の自社権限管理とは別の運営画面です。
        </p>
      </div>
      <CButton class="page-header__action" color="primary" @click="openCreate">
        契約企業を追加
      </CButton>
    </div>

    <CAlert color="info" class="provider-scope-alert">
      この画面はPREIKA運営用です。契約企業管理者は自社内では最上位ですが、
      他社・契約プラン・PREIKA運営権限を変更できません。
    </CAlert>
    <CAlert v-if="successMessage" color="success" close-button @update:show="successMessage = ''">
      {{ successMessage }}
    </CAlert>
    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>

    <CCard class="access-card">
      <CCardHeader class="section-header">
        <strong>契約企業</strong>
        <CButton
          class="section-header__action"
          size="sm"
          color="secondary"
          variant="outline"
          :disabled="loading"
          @click="loadAccounts"
        >
          再読込
        </CButton>
      </CCardHeader>
      <CCardBody>
        <div v-if="loading" class="text-center py-5"><CSpinner color="primary"/></div>
        <template v-else>
          <div class="desktop-only responsive-table-shell">
            <CDataTable
              :items="normalizedAccounts"
              :fields="accountFields"
              hover
              striped
              bordered
              pagination
              :items-per-page="10"
              no-items-view="契約企業がありません。"
            >
              <template #actions="{item}">
                <td class="py-2 action-cell">
                  <CButton size="sm" color="info" variant="outline" @click="selectAccount(item)">
                    管理
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>

          <div class="mobile-only mobile-record-list" aria-label="契約企業一覧">
            <article v-for="account in normalizedAccounts" :key="account.id" class="mobile-record-card">
              <div class="mobile-record-card__header">
                <strong>{{ account.name }}</strong>
                <CBadge color="secondary">{{ account.contractStatusLabel }}</CBadge>
              </div>
              <dl class="mobile-detail-list">
                <div><dt>契約企業</dt><dd>{{ account.contractorName }}</dd></div>
                <div><dt>契約期間</dt><dd>{{ account.contractPeriodLabel }}</dd></div>
                <div><dt>販売代理店</dt><dd>{{ account.resellerName }}</dd></div>
                <div><dt>同じ契約の会社数</dt><dd>{{ account.tenantCountLabel }}</dd></div>
                <div><dt>契約プラン</dt><dd>{{ account.planName }}</dd></div>
                <div><dt>利用人数上限</dt><dd>{{ account.userLimitLabel }}</dd></div>
              </dl>
              <CButton class="mobile-primary-action" color="info" variant="outline" @click="selectAccount(account)">
                この企業を管理
              </CButton>
            </article>
            <div v-if="!normalizedAccounts.length" class="mobile-empty-state">
              契約企業がありません。
            </div>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <CCard v-if="selectedAccount" class="access-card selected-account-card">
      <CCardHeader class="selected-account-header">
        <div>
          <strong>{{ selectedAccount.name }} のProvider管理</strong>
          <small>契約企業側の利用者・役割設定は、自社権限管理で行います。</small>
        </div>
      </CCardHeader>
      <CCardBody>
        <CTabs class="access-tabs">
          <!--
            **一時パス（場面 S-95）。**

            出せるのは PREIKA運営だけ。顧客企業の管理者の画面には
            置かない——置くと、会社側の管理者が自分の第2要素を
            迂回する資格情報を自分で作れる。

            **入口が開いているときだけタブを出す。**閉じているのに
            出すと、押しても必ず断られる。「機能はOFF」なのに
            壊れた機能に見える。
          -->
          <CTab v-if="temporaryPassEnabled" title="一時パス">
            <div class="pt-3">
              <p class="text-muted">
                二段階認証を両方失った方へ、電話で読み上げて渡す
                一時パスを出します。<b>この会社の管理者には出せません。</b>
              </p>
              <!--
                **数字を打たせない（場面 S-95）。**

                一時パスは<b>その人になりすませる文字列</b>。対象を内部の番号の
                転記で決めると、桁を1つ打ち間違えただけで<b>同じ会社の別の人へ
                正しく発行される。</b>会社の境目はサーバが守っているので、
                そこでは止まらない。

                **番号自体が間違っているだけ**なので、サーバには検知しようが
                ない。人が取り違えないようにするのは、ここの仕事。
              -->
              <CSelect
                class="platform-issue-target"
                label="対象の利用者"
                :value="issueUserId === null ? '' : String(issueUserId)"
                :options="temporaryPassUserOptions"
                placeholder="選んでください"
                description="氏名とメールアドレスで確かめてから選んでください。"
                @update:value="selectTemporaryPassUser"
              />
              <p v-if="!normalizedTemporaryPassUsers.length" class="text-muted">
                この会社の利用者を読み込めませんでした。
                <b>読み込めないあいだは出せません。</b>
              </p>
              <CButton
                class="platform-issue-pass"
                color="primary"
                variant="outline"
                :disabled="!issueUserId"
                @click="startIssueTemporaryPass()"
              >一時パスを出す</CButton>
            </div>
          </CTab>

          <CTab title="契約" active>
            <CRow class="pt-3 contract-form-row">
              <CCol sm="12" md="4">
                <!-- **v-model は CSelect では死ぬ。**選んでも送られない。 -->
                <CSelect
                  label="契約プラン"
                  :options="planOptions"
                  :value="contractForm.planId"
                  @update:value="value => { contractForm.planId = selectedValue(value) }"
                />
              </CCol>
              <CCol sm="12" md="4">
                <CSelect
                  label="契約状態"
                  :options="statusOptions"
                  :value="contractForm.contractStatus"
                  @update:value="value => { contractForm.contractStatus = selectedValue(value) }"
                />
              </CCol>
              <CCol sm="12" md="4">
                <CInput
                  label="利用人数上限"
                  type="number"
                  min="1"
                  v-model.number="contractForm.userLimit"
                  description="未入力の場合は無制限です。"
                />
              </CCol>
            </CRow>
            <!--
              **期間は機能の門番。**外れるとその会社の機能が全部落ちる。
              いままで画面に1文字も出ておらず、入れる口も無かった
              （入れられないのに、入っていたら止まる状態だった）。
            -->
            <CRow class="contract-form-row">
              <CCol sm="12" md="4">
                <CInput
                  label="契約開始日"
                  type="date"
                  v-model="contractForm.contractStartDate"
                  description="未入力なら開始日の制限はありません。"
                />
              </CCol>
              <CCol sm="12" md="4">
                <CInput
                  label="契約終了日"
                  type="date"
                  v-model="contractForm.contractEndDate"
                  :description="endDateWarning"
                />
              </CCol>
            </CRow>
            <CButton class="primary-action" color="primary" :disabled="saving" @click="saveContract">
              契約を更新
            </CButton>
          </CTab>

          <!--
            契約に関わる会社。**「契約」タブの隣に置く。**
            契約企業・請求先・販売代理店・代行者は契約の一部で、
            テナントが扱う取引先（クライアント）とは別のもの。
          -->
          <CTab title="契約に関わる会社">
            <ContractParties :account-id="selectedAccount.id"/>
          </CTab>

          <CTab title="利用可能機能">
            <div class="pt-3">
              <CAlert color="secondary">
                ここで契約企業全体のFeatureを設定します。各スタッフが実際に利用できる操作は、
                契約企業側の役割・Permissionでも絞り込まれます。
              </CAlert>
              <div class="desktop-only responsive-table-shell">
                <CDataTable :items="normalizedFeatures" :fields="featureFields" bordered striped>
                  <template #enabled="{item}">
                    <td class="switch-cell">
                      <CSwitch
                        color="success"
                        :checked="item.enabled"
                        @update:checked="value => changeFeature(item, value)"
                      />
                    </td>
                  </template>
                </CDataTable>
              </div>

              <div class="mobile-only mobile-record-list" aria-label="利用可能機能一覧">
                <article v-for="feature in normalizedFeatures" :key="feature.featureCode" class="mobile-record-card mobile-feature-card">
                  <div class="mobile-record-card__header">
                    <strong>{{ feature.featureName }}</strong>
                    <CBadge color="secondary">{{ feature.sourceLabel }}</CBadge>
                  </div>
                  <div class="mobile-switch-row">
                    <span>{{ feature.enabled ? '利用可' : '利用不可' }}</span>
                    <CSwitch
                      color="success"
                      :checked="feature.enabled"
                      @update:checked="value => changeFeature(feature, value)"
                    />
                  </div>
                </article>
                <div v-if="!normalizedFeatures.length" class="mobile-empty-state">
                  利用可能機能がありません。
                </div>
              </div>
            </div>
          </CTab>

          <CTab title="最初の管理者招待">
            <div class="pt-3">
              <div v-if="invitationLoading" class="text-center py-5"><CSpinner color="primary"/></div>
              <template v-else>
                <CAlert color="info">
                  最初の契約企業管理者は、メールの期限付きリンクから本人がパスワードを設定して受諾します。
                  AKI側で初期パスワードを作成・共有しません。
                </CAlert>

                <section v-if="ownerInvitation" class="invitation-card">
                  <div class="invitation-card__header">
                    <div>
                      <p>契約企業管理者（自社最上位）</p>
                      <h3>{{ ownerInvitation.invitedName || '氏名未設定' }}</h3>
                    </div>
                    <CBadge :color="invitationStatusColor(ownerInvitation.status)">
                      {{ invitationStatusLabel(ownerInvitation.status, ownerInvitation.emailSent) }}
                    </CBadge>
                  </div>

                  <dl class="invitation-detail-list">
                    <div><dt>メールアドレス</dt><dd>{{ ownerInvitation.maskedEmail || '確認できません' }}</dd></div>
                    <div><dt>受諾期限</dt><dd>{{ formatDateTime(ownerInvitation.expiresAt) }}</dd></div>
                    <div><dt>最終送信日時</dt><dd>{{ formatDateTime(ownerInvitation.sentAt) }}</dd></div>
                    <div><dt>送信回数</dt><dd>{{ ownerInvitation.sentCount == null ? '0' : ownerInvitation.sentCount }}回</dd></div>
                  </dl>

                  <p v-if="ownerInvitation.message" class="invitation-message">{{ ownerInvitation.message }}</p>

                  <div class="invitation-actions">
                    <CButton
                      color="primary"
                      variant="outline"
                      :disabled="saving || !canResendInvitation"
                      @click="resendInvitation"
                    >
                      招待メールを再送
                    </CButton>
                    <CButton
                      color="info"
                      variant="outline"
                      :disabled="saving || !canChangeInvitation"
                      @click="openReplaceInvitation"
                    >
                      招待先を変更
                    </CButton>
                    <CButton
                      color="danger"
                      variant="outline"
                      :disabled="saving || !canChangeInvitation"
                      @click="revokeInvitation"
                    >
                      招待を取消
                    </CButton>
                  </div>
                </section>

                <div v-else class="mobile-empty-state">
                  招待情報を取得できませんでした。
                </div>
              </template>
            </div>
          </CTab>

          <CTab title="操作履歴">
            <div class="pt-3">
              <div class="desktop-only responsive-table-shell">
                <CDataTable
                  :items="normalizedAuditLogs"
                  :fields="auditFields"
                  bordered
                  striped
                  pagination
                  :items-per-page="10"
                />
              </div>

              <div class="mobile-only mobile-record-list" aria-label="操作履歴一覧">
                <article v-for="(log, index) in normalizedAuditLogs" :key="`${log.createdAt}-${index}`" class="mobile-record-card">
                  <div class="mobile-record-card__header">
                    <strong>{{ log.actionLabel }}</strong>
                    <span class="mobile-record-card__date">{{ log.createdAt || '日時未記録' }}</span>
                  </div>
                  <dl class="mobile-detail-list">
                    <div><dt>操作者</dt><dd>{{ log.actorName }}</dd></div>
                    <div><dt>対象</dt><dd>{{ log.targetLabel }}</dd></div>
                  </dl>
                </article>
                <div v-if="!normalizedAuditLogs.length" class="mobile-empty-state">
                  操作履歴がありません。
                </div>
              </div>
            </div>
          </CTab>
        </CTabs>
      </CCardBody>
    </CCard>

    <CModal
      title="契約企業の新規作成と管理者招待"
      :show.sync="createModal"
      size="lg"
      :close-on-backdrop="false"
      class="access-modal"
    >
      <CAlert color="info">
        企業・契約を作成した後、最初の契約企業管理者へ期限付き招待メールを送信します。
        パスワードは本人が招待受諾画面で設定します。
      </CAlert>

      <h6 class="form-block-title">会社・契約</h6>
      <!--
        **必須の項目には印を付ける。**印が無いと、任意の電話番号や
        契約プランと見た目が同じになる。全部入れたつもりの人には、
        押せないボタンだけが残る。
      -->
      <CRow>
        <CCol sm="12" md="6">
          <label>会社名<span class="asterisk">*</span></label>
          <CInput v-model.trim="createForm.companyName"/>
        </CCol>
        <CCol sm="12" md="6">
          <label>会社メール<span class="asterisk">*</span></label>
          <CInput type="email" v-model.trim="createForm.companyEmail"/>
        </CCol>
        <CCol sm="12" md="6"><CInput label="電話番号" v-model.trim="createForm.telephone"/></CCol>
        <CCol sm="12" md="6">
          <CSelect
            label="契約プラン"
            :options="planCodeOptions"
            :value="createForm.planCode"
            @update:value="value => { createForm.planCode = selectedValue(value) }"
          />
        </CCol>
        <CCol sm="12" md="6">
          <CSelect
            label="契約状態"
            :options="newStatusOptions"
            :value="createForm.contractStatus"
            @update:value="value => { createForm.contractStatus = selectedValue(value) }"
          />
        </CCol>
        <CCol sm="12" md="6">
          <CInput
            label="利用人数上限"
            type="number"
            min="1"
            v-model.number="createForm.userLimit"
            description="未入力の場合は無制限です。"
          />
        </CCol>
      </CRow>

      <h6 class="form-block-title">連絡先（役割とは別管理）</h6>
      <!--
        **空のまま作らせない。**契約タブに連絡先の入力欄は無く、契約の
        更新はプラン・契約状態・利用人数上限しか受け取らない。つまり
        **ここで入れそこねると、あとから画面では直せない。**

        とはいえ3つとも必須にすると、同じアドレスを3回打たせることに
        なる。未入力なら会社メールで埋めて、**届く先を必ず1つ残す。**
      -->
      <CRow>
        <CCol sm="12" md="6">
          <CInput
            label="契約連絡先メール"
            type="email"
            v-model.trim="createForm.contractContactEmail"
            description="未入力の場合は会社メールを使います。"
          />
        </CCol>
        <CCol sm="12" md="6">
          <CInput
            label="請求連絡先メール"
            type="email"
            v-model.trim="createForm.billingContactEmail"
            description="未入力の場合は会社メールを使います。"
          />
        </CCol>
        <CCol sm="12" md="6">
          <CInput
            label="セキュリティ連絡先メール"
            type="email"
            v-model.trim="createForm.securityContactEmail"
            description="未入力の場合は会社メールを使います。"
          />
        </CCol>
      </CRow>

      <h6 class="form-block-title">最初の契約企業管理者</h6>
      <CRow>
        <CCol sm="12" md="6">
          <label>氏名<span class="asterisk">*</span></label>
          <CInput v-model.trim="createForm.ownerName"/>
        </CCol>
        <CCol sm="12" md="6">
          <label>メールアドレス<span class="asterisk">*</span></label>
          <CInput type="email" v-model.trim="createForm.ownerEmail"/>
        </CCol>
        <CCol sm="12" md="6">
          <label>社員番号<span class="asterisk">*</span></label>
          <CInput v-model.trim="createForm.ownerEmployeeId"/>
        </CCol>
      </CRow>
      <p class="form-note">
        招待受諾後、この方は自社利用者・役割・権限・会社設定を管理できます。
        PREIKA全体の運営権限は付与されません。
      </p>

      <!--
        **押せない理由は、押せないボタンと同じ幕の中に出す。**

        errorMessage は画面のいちばん上に出る。モーダルが開いている
        あいだ、そこは幕の裏で読めない。作成できない理由をそこへ書いても
        **運営には届かない。**
      -->
      <CAlert v-if="missingFields.length" color="warning" class="mb-3">
        <b>あと{{ missingFields.length }}つ入力すると、作成できます。</b>
        <ul class="mb-0 mt-2">
          <li v-for="what in missingFields" :key="what">{{ what }}</li>
        </ul>
      </CAlert>

      <!--
        **断られた理由も、同じ幕の中に出す。**押せなかった理由だけを
        幕の中に出しても、押せたあと断られた理由が画面上端では、
        <b>押しても画面が1ドットも変わらない</b>ままになる。運営には
        押せていないのか断られたのかが分からない。
      -->
      <CAlert v-if="createError" color="danger" class="mb-3">
        {{ createError }}
      </CAlert>

      <template #footer>
        <div class="modal-actions">
          <CButton color="secondary" @click="createModal = false">キャンセル</CButton>
          <CButton
            class="create-submit"
            color="primary"
            :disabled="saving || missingFields.length > 0"
            @click="createAccount"
          >
            {{ saving ? '作成しています' : '作成して招待メールを送信' }}
          </CButton>
        </div>
      </template>
    </CModal>

    <CModal
      title="招待先を変更"
      :show.sync="replaceInvitationModal"
      :close-on-backdrop="false"
      class="access-modal"
    >
      <CAlert color="warning">
        現在の招待リンクは無効になります。新しい担当者へ新しい期限付き招待メールを送信します。
      </CAlert>
      <label>氏名<span class="asterisk">*</span></label>
      <CInput v-model.trim="replaceInvitationForm.ownerName"/>
      <label>メールアドレス<span class="asterisk">*</span></label>
      <CInput type="email" v-model.trim="replaceInvitationForm.ownerEmail"/>
      <!--
        **社員番号はここでは必須にしない。**利用者の登録・更新では任意
        （employee|max:20）で、受諾後に利用者の更新から入れられる。
        ここだけ必須にすると、まだ番号の決まっていない担当者へ招待を
        出し直せない。
      -->
      <CInput
        label="社員番号"
        v-model.trim="replaceInvitationForm.ownerEmployeeId"
        description="任意です。受諾後に利用者の更新からも入れられます。"
      />

      <!--
        **押せない理由は、押せないボタンと同じ幕の中に出す。**
        前は空のまま押すと早期returnで戻るだけで、画面が1ドットも
        変わらなかった。
      -->
      <CAlert v-if="replaceMissingFields.length" color="warning" class="mb-3">
        <b>あと{{ replaceMissingFields.length }}つ入力すると、変更できます。</b>
        <ul class="mb-0 mt-2">
          <li v-for="what in replaceMissingFields" :key="what">{{ what }}</li>
        </ul>
      </CAlert>

      <!--
        **断られた理由も、同じ幕の中に出す。**ここは<b>いまの招待リンクを
        捨てる</b>操作。通ったのか断られたのかが分からないまま閉じられると、
        新しい担当者にも古い担当者にも届いていない状態を疑えない。
      -->
      <CAlert v-if="replaceError" color="danger" class="mb-3">
        {{ replaceError }}
      </CAlert>

      <template #footer>
        <div class="modal-actions">
          <CButton color="secondary" @click="replaceInvitationModal = false">キャンセル</CButton>
          <CButton
            class="replace-invitation-submit"
            color="primary"
            :disabled="saving || replaceMissingFields.length > 0"
            @click="replaceInvitation"
          >
            変更して招待を再送
          </CButton>
        </div>
      </template>
    </CModal>
    <!-- 出す前に確かめる（場面 S-95）。取り返しがつかない。 -->
    <CModal
      :show="Boolean(issueTarget) && !issuedPass"
      title="一時パスを出します"
      color="primary"
      @update:show="cancelIssueTemporaryPass"
    >
      <!--
        **IDだけを最終確認に使わない。**番号は人が照合できない。
        氏名とメールアドレス、そしてどの会社かまで出して、
        <b>電話の相手と突き合わせられる形</b>にする。
      -->
      <div v-if="issueTargetUser" class="platform-issue-confirm">
        <p class="platform-issue-confirm__name">
          <b>{{ issueTargetUser.name }}</b>
        </p>
        <p class="platform-issue-confirm__email">
          {{ issueTargetUser.email }}
        </p>
        <p class="platform-issue-confirm__company">
          {{ selectedAccount ? selectedAccount.name : '' }}
        </p>
        <p>に一時パスを発行します。</p>
        <p class="text-muted">利用者ID {{ issueTarget }}</p>
      </div>

      <!--
        **警告であって、止める材料ではない。**社内に救える別の人が
        居ても出せる。止めると、その人が休みだったり連絡がつかなかったり
        する場面で誰も救えなくなる。居るかどうかはサーバが数える。
      -->
      <CAlert v-if="alternativeRecoveryAvailable" color="warning">
        社内管理者による通常の復旧経路が利用できる可能性があります。
        それでも一時パスを発行する場合は、理由を入力してください。
      </CAlert>

      <CAlert v-if="livePassId" color="info">
        この方には、まだ使える一時パスがあります。
        新しく出すと、<b>いまの1本は使えなくなります。</b>
      </CAlert>

      <label>理由（記録に残ります）<span class="asterisk">*</span></label>
      <CInput
        v-model="issueReason"
        placeholder="例: 端末を紛失、ほかに外せる管理者が居ない"
        maxlength="200"
      />
      <!--
        **自由入力の数字にしない。**使える時間はサーバが範囲を決めて
        いるのに、画面側は何分でも打てた。範囲外を打った運営には
        「出せませんでした」しか返らない——電話口で本人を待たせたまま、
        直し方の分からない行き止まりになる。**打てない形にする。**
      -->
      <CSelect
        class="platform-issue-lifetime"
        label="使える時間"
        :value="String(issueLifetimeMinutes)"
        :options="lifetimeMinuteOptions"
        @update:value="selectLifetimeMinutes"
      />

      <!--
        **押せない理由は、押せないボタンと同じ幕の中に出す。**
        理由は必須だが、空のときに出ていたのは無効なボタンだけだった。
      -->
      <CAlert v-if="issueMissingFields.length" color="warning" class="mb-3">
        <b>あと{{ issueMissingFields.length }}つ入力すると、出せます。</b>
        <ul class="mb-0 mt-2">
          <li v-for="what in issueMissingFields" :key="what">{{ what }}</li>
        </ul>
      </CAlert>

      <template #footer>
        <CButton color="secondary" @click="cancelIssueTemporaryPass">
          やめる
        </CButton>
        <CButton
          class="platform-issue-submit"
          color="primary"
          :disabled="issueMissingFields.length > 0"
          @click="confirmIssueTemporaryPass"
        >出す</CButton>
      </template>
    </CModal>

    <!--
      **出した文字列は、ここでしか出てこない。**DBには引く鍵しか
      入っていない。閉じたら二度と見られない。
    -->
    <CModal
      :show="Boolean(issuedPass)"
      title="一時パスを出しました"
      color="primary"
      :closeOnBackdrop="false"
      @update:show="closeIssuedPass"
    >
      <p>
        <b>この画面を閉じると、二度と見られません。</b>
        電話で読み上げてから閉じてください。
      </p>
      <p class="platform-issued-pass">{{ issuedPass }}</p>
      <p v-if="issuedExpiresAt" class="text-muted">
        使える期限: {{ issuedExpiresAt }}
      </p>
      <p class="text-muted">
        本人にも「一時パスが発行されました」という知らせが届きます
        （<b>文字列そのものは書かれていません</b>）。
      </p>
      <template #footer>
        <CButton color="primary" @click="closeIssuedPass">
          読み上げました
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ContractParties from './ContractParties.vue'
import api from '@/services/accessManagement'
import { safeErrorDigest } from '../../utils/safeResponseMessage'
import { selectedValue } from '@/utils/coreuiSelect'

const emptyCreateForm = () => ({
  companyName: '',
  companyEmail: '',
  telephone: '',
  contractContactEmail: '',
  billingContactEmail: '',
  securityContactEmail: '',
  planCode: 'STANDARD',
  contractStatus: 'ACTIVE',
  userLimit: null,
  ownerName: '',
  ownerEmail: '',
  ownerEmployeeId: ''
})

const emptyReplaceInvitationForm = () => ({
  ownerName: '', ownerEmail: '', ownerEmployeeId: ''
})

const STATUS_LABELS = {
  PREPARING: '利用開始前', TRIAL: '無料試用中', ACTIVE: '利用中',
  SUSPENDED: '利用停止中', CANCELLED: '解約済み', EXPIRED: '契約終了'
}

const INVITATION_STATUS_LABELS = {
  PENDING: '受諾待ち', ACCEPTED: '受諾済み', EXPIRED: '期限切れ',
  REVOKED: '取消済み', EMAIL_SEND_FAILED: 'メール未送信'
}

const SOURCE_LABELS = { PLAN: '契約プラン設定', OVERRIDE: '個別設定' }

/**
 * 一時パスの使える時間（分）の選択肢。
 *
 * **選ばせる形にするのは、自由入力だと範囲外を打てるから。**サーバは
 * 決まった範囲しか受け付けないので、範囲外を打った運営には「出せません
 * でした」しか返らない。電話口で本人を待たせたまま、直し方の分からない
 * 行き止まりになる。
 */
const LIFETIME_MINUTE_CHOICES = [15, 30, 60]

/** 何も選ばなくても出せるように、既定を持つ。 */
const DEFAULT_LIFETIME_MINUTES = 30

const ACTION_LABELS = {
  ACCOUNT_CREATE: '契約企業を作成', CONTRACT_UPDATE: '契約情報を更新',
  FEATURE_UPDATE: '利用可能機能を更新', TENANT_ROLE_CREATE: 'ロールを作成',
  TENANT_ROLE_PERMISSION_UPDATE: 'ロール権限を更新', TENANT_USER_ROLE_ASSIGN: 'ロールを付与',
  TENANT_USER_ROLE_REMOVE: 'ロールを解除',
  INITIAL_OWNER_INVITATION_RESEND: '最初の管理者招待を再送',
  INITIAL_OWNER_INVITATION_REPLACE: '最初の管理者招待先を変更',
  INITIAL_OWNER_INVITATION_REVOKE: '最初の管理者招待を取消',
  USER_INVITATION_ACCEPT: '管理者招待を受諾'
}

export default {
  components: { ContractParties },
  name: 'PlatformContractManagement',
  data () {
    return {
      loading: false,
      invitationLoading: false,
      saving: false,
      initialized: false,
      errorMessage: '',
      successMessage: '',
      createModal: false,
      replaceInvitationModal: false,

      /*
       * **幕の中で出す文言は、画面上端のものと分けて持つ。**
       *
       * errorMessage を出す CAlert は画面のいちばん上にあり、モーダルが
       * 開いているあいだは暗幕の下で読めない。この2つは**断られても
       * 幕を開いたままにする**——打った内容を捨てないため——ので、
       * そこへ書くと運営には届かない。押したのに画面が1ドットも
       * 変わらず、押せなかったときと見分けが付かなくなる。
       *
       * 一時パスの確認だけは、断られたら幕を閉じるので上端でよい。
       */
      createError: '',
      replaceError: '',
      accounts: [],

      /**
       * 一時パスの入口が開いているか（場面 S-95）。
       *
       * **サーバが決める。**読めなかったときも false のまま——
       * 出して押させて断られるより、出ないほうがよい。
       */
      temporaryPassEnabled: false,

      /**
       * この会社の利用者。**選ばせるために読む。**
       *
       * 番号を打たせると、桁を1つ間違えただけで同じ会社の別の人へ
       * 正しく発行される。会社の境目はサーバが守るが、**番号自体が
       * 間違っているだけ**なので、そこでは止まらない。
       */
      temporaryPassUsers: [],

      /** 出す相手の利用者ID。**一覧から選んだものだけが入る。** */
      issueUserId: null,

      /** 確認を出している相手。null なら出していない。 */
      issueTarget: null,

      /** なぜ出すのか。**必須。** */
      issueReason: '',

      /** 何分もたせるか。上限と下限はサーバが決める。 */
      issueLifetimeMinutes: DEFAULT_LIFETIME_MINUTES,

      /** いま生きている1本の id。**出すときにそのまま送る。** */
      livePassId: null,

      /** 社内に救える別の人が居るか（**警告のためだけ**）。 */
      alternativeRecoveryAvailable: false,

      /** 出した文字列。**ここでしか出てこない。** */
      issuedPass: '',

      /** 出した1本の期限。 */
      issuedExpiresAt: '',
      plans: [],
      features: [],
      auditLogs: [],
      ownerInvitation: null,
      selectedAccount: null,
      createForm: emptyCreateForm(),
      replaceInvitationForm: emptyReplaceInvitationForm(),
      contractForm: {
        accountId: null,
        planId: null,
        contractStatus: 'ACTIVE',
        userLimit: null,
        contractStartDate: '',
        contractEndDate: ''
      },
      accountFields: [
        { key: 'name', label: '会社名' },
        // **契約企業と販売代理店は、一覧で見えないと意味がない。**
        // どの契約が代理店経由かは、開いて確かめるものではなく
        // 並べて見るもの（手数料を払う相手を数えるため）。
        { key: 'contractorName', label: '契約企業' },
        { key: 'resellerName', label: '販売代理店' },
        { key: 'tenantCountLabel', label: '同じ契約の会社数' },
        { key: 'planName', label: '契約プラン' },
        { key: 'contractStatusLabel', label: '契約状態' },
        { key: 'contractPeriodLabel', label: '契約期間' },
        { key: 'userLimitLabel', label: '利用人数上限' },
        { key: 'actions', label: '' }
      ],
      featureFields: [
        { key: 'featureName', label: '機能' },
        { key: 'sourceLabel', label: '設定方法' },
        { key: 'enabled', label: '利用可否' }
      ],
      auditFields: [
        { key: 'createdAt', label: '日時' },
        { key: 'actionLabel', label: '操作内容' },
        { key: 'actorName', label: '操作者' },
        { key: 'targetLabel', label: '対象' }
      ],
      statusOptions: [
        { value: 'PREPARING', label: '利用開始前' },
        { value: 'TRIAL', label: '無料試用中' },
        { value: 'ACTIVE', label: '利用中' },
        { value: 'SUSPENDED', label: '利用停止中' },
        { value: 'CANCELLED', label: '解約済み' },
        { value: 'EXPIRED', label: '契約終了' }
      ],
      newStatusOptions: [
        { value: 'PREPARING', label: '利用開始前' },
        { value: 'TRIAL', label: '無料試用中' },
        { value: 'ACTIVE', label: '利用中' }
      ]
    }
  },
  computed: {
    /**
     * 終了日の欄に出す説明。
     *
     * **過ぎているときは、それが原因だと分かるように言う。**
     * 期間を外れるとその会社の機能が全部落ちるのに、
     * 画面には理由が出ないままだった。
     */
    endDateWarning () {
      const end = String(this.contractForm.contractEndDate || '').slice(0, 10)
      if (!end) return '未入力なら無期限です。'
      const today = new Date()
      const iso = today.getFullYear() + '-' +
        String(today.getMonth() + 1).padStart(2, '0') + '-' +
        String(today.getDate()).padStart(2, '0')
      if (end < iso) {
        return '⚠ この日は過ぎています。この会社は全機能が使えません。'
      }
      return 'この日を過ぎると、この会社は全機能が使えなくなります。'
    },

    normalizedAccounts () {
      return this.accounts.map(item => {
        const id = this.value(item, 'accountId', 'account_id', 'id')
        const limit = this.value(item, 'userLimit', 'user_limit')
        const status = this.value(item, 'contractStatus', 'contract_status')
        return {
          raw: item,
          id,
          name: this.value(item, 'name', 'accountName', 'account_name', 'companyName', 'company_name') || '名称未設定の契約企業',
          planId: this.value(item, 'planId', 'plan_id'),
          planCode: this.value(item, 'planCode', 'plan_code'),
          planName: this.value(item, 'planName', 'plan_name') || '未設定',
          contractStatus: status,
          contractStatusLabel: STATUS_LABELS[status] || '未設定',
          contractStartDate: this.dateValue(item, 'contractStartDate', 'contract_start_date'),
          contractEndDate: this.dateValue(item, 'contractEndDate', 'contract_end_date'),
          contractPeriodLabel: this.periodLabel(
            this.dateValue(item, 'contractStartDate', 'contract_start_date'),
            this.dateValue(item, 'contractEndDate', 'contract_end_date')),
          userLimit: limit,
          userLimitLabel: limit == null ? '無制限' : `${limit}名`,

          // **空欄と『無い』を書き分ける。**代理店が空なら直販、
          // 契約企業が空なら契約へまだ結ばれていない、で意味が違う。
          contractorName: this.value(item, 'contractorName', 'contractor_name') || '未登録',
          resellerName: this.value(item, 'resellerName', 'reseller_name') || '直販',
          tenantCountLabel: `${this.value(item, 'tenantCount', 'tenant_count') || 1}社`
        }
      })
    },
    normalizedFeatures () {
      return this.features.map(item => ({
        id: this.value(item, 'id'),
        featureCode: this.value(item, 'featureCode', 'feature_code'),
        featureName: this.value(item, 'featureName', 'feature_name') || '名称未設定の機能',
        enabled: Boolean(this.value(item, 'enabled')),
        sourceLabel: SOURCE_LABELS[this.value(item, 'source')] || '未設定'
      }))
    },
    normalizedAuditLogs () {
      return this.auditLogs.map(item => {
        const action = this.value(item, 'actionCode', 'action_code')
        const targetType = this.value(item, 'targetType', 'target_type')
        return {
          createdAt: this.value(item, 'createdAt', 'created_at'),
          actionLabel: ACTION_LABELS[action] || '設定を変更',
          actorName: this.value(item, 'actorName', 'actor_name') || 'PREIKA運営管理者',
          targetLabel: this.targetTypeLabel(targetType)
        }
      })
    },
    planOptions () {
      return this.plans.map(plan => ({
        value: this.value(plan, 'id'),
        label: this.value(plan, 'planName', 'plan_name') || '名称未設定'
      }))
    },
    planCodeOptions () {
      return this.plans.map(plan => ({
        value: this.value(plan, 'planCode', 'plan_code'),
        label: this.value(plan, 'planName', 'plan_name') || '名称未設定'
      }))
    },
    /**
     * 契約企業の新規作成で、まだ入っていない必須項目の名前。
     *
     * <p>画面に出ている言葉をそのまま使う。中の変数名を出しても、
     * <b>どこを直せばよいか分からない。</b></p>
     */
    missingFields () {
      /*
       * **押せない条件は、この一覧そのもの。**作成ボタンの :disabled は
       * missingFields.length しか見ていない。同じ意味の条件をもう1つ
       * 書くと、片方だけ直したときに「あと0つです」と出ているのに
       * 押せない、というさらに分かりにくい形になる。
       *
       * 3種類の連絡先メールはここに入れない。**未入力なら会社メールで
       * 埋める**ので、足りない項目ではない。
       */
      const missing = []
      if (!this.createForm.companyName) { missing.push('会社名') }
      if (!this.createForm.companyEmail) { missing.push('会社メール') }
      if (!this.createForm.ownerName) {
        missing.push('氏名（最初の契約企業管理者）')
      }
      if (!this.createForm.ownerEmail) {
        missing.push('メールアドレス（最初の契約企業管理者）')
      }
      if (!this.createForm.ownerEmployeeId) {
        missing.push('社員番号（最初の契約企業管理者）')
      }
      return missing
    },

    /**
     * 招待先の変更で、まだ入っていない必須項目の名前。
     *
     * <p><b>社員番号は入れない。</b>利用者の登録・更新では任意で、
     * 受諾後に利用者の更新から入れられる。ここだけ必須にすると、
     * まだ番号の決まっていない担当者へ招待を出し直せない。</p>
     */
    replaceMissingFields () {
      /*
       * **押せない条件は、この一覧そのもの。**変更ボタンの :disabled は
       * replaceMissingFields.length しか見ていない。
       */
      const missing = []
      if (!this.replaceInvitationForm.ownerName) { missing.push('氏名') }
      if (!this.replaceInvitationForm.ownerEmail) {
        missing.push('メールアドレス')
      }
      return missing
    },

    /**
     * 一時パスを出すのに、まだ入っていない必須項目の名前。
     *
     * <p>使える時間は入れない。<b>選択肢から選ぶ形にして、既定の
     * {@link DEFAULT_LIFETIME_MINUTES} 分のまま出せる。</b></p>
     */
    issueMissingFields () {
      /*
       * **押せない条件は、この一覧そのもの。**出すボタンの :disabled は
       * issueMissingFields.length しか見ていない。
       */
      const missing = []
      if (!String(this.issueReason || '').trim()) {
        missing.push('理由（記録に残ります）')
      }
      return missing
    },

    /** 使える時間の選択肢。**部品は文字列で扱うので、文字列で渡す。** */
    lifetimeMinuteOptions () {
      return LIFETIME_MINUTE_CHOICES.map(minutes => ({
        value: String(minutes), label: `${minutes}分`
      }))
    },

    canChangeInvitation () {
      return Boolean(this.ownerInvitation && ['PENDING', 'EMAIL_SEND_FAILED', 'EXPIRED'].includes(this.ownerInvitation.status))
    },
    /**
     * サーバが返した利用者を、画面で使う形へそろえる。
     *
     * <p><b>返ってくるキーは user_id / employee_id。</b>id でも
     * employeeCode でもない。合わせずに書くと value が undefined に
     * なり、<b>一覧は出るのに誰も選べない。</b>実際そうなっていた。</p>
     *
     * <p>いくつかの綴りを見るのは、この口が将来キーを変えたときに
     * 黙って選べなくなるのを避けるため。<b>id が取れない行は捨てる</b>
     * ——選べない選択肢を出しても、押して初めて分かるだけ。</p>
     */
    normalizedTemporaryPassUsers () {
      return this.temporaryPassUsers
        .map(user => ({
          id: this.value(user, 'user_id', 'id', 'userId'),
          name: this.value(user, 'name'),
          email: this.value(user, 'email'),
          employeeCode: this.value(
            user, 'employee_id', 'employeeCode', 'employee_code')
        }))
        .filter(user => user.id !== null && user.id !== undefined)
    },

    /**
     * 選択肢。**氏名とメールアドレスを必ず出す。**
     *
     * <p>番号だけでは人が照合できない。電話の相手と突き合わせられる
     * 形にする。社員番号があれば添える。</p>
     */
    temporaryPassUserOptions () {
      return [{ value: '', label: '選んでください' }].concat(
        this.normalizedTemporaryPassUsers.map(user => ({
          value: String(user.id),
          label: [
            user.name,
            user.email,
            user.employeeCode ? '社員番号 ' + user.employeeCode : null,
            '利用者ID ' + user.id
          ].filter(Boolean).join(' / ')
        }))
      )
    },

    /** いま選ばれている人。**確認画面はこれを出す。** */
    issueTargetUser () {
      if (this.issueTarget === null) { return null }
      return this.normalizedTemporaryPassUsers.find(
        user => Number(user.id) === Number(this.issueTarget)) || null
    },

    canResendInvitation () {
      return Boolean(this.ownerInvitation && ['PENDING', 'EMAIL_SEND_FAILED', 'EXPIRED'].includes(this.ownerInvitation.status))
    }
  },
  mounted () {
    this.initialize()
    this.loadTemporaryPassStatus()
  },
  methods: {
    /**
     * 日付を yyyy-MM-dd で取り出す。
     *
     * サーバは '2026-04-01' のことも '2026-04-01T00:00:00' のことも
     * あるので、頭の10文字だけを見る。**日付入力欄は yyyy-MM-dd しか
     * 受け付けない**ので、時刻が付いたまま入れると欄が空のまま描かれる
     * （入っているのに空に見える）。
     */
    dateValue (item, ...keys) {
      const v = this.value(item, ...keys)
      if (v === null || v === undefined || v === '') return ''
      return String(v).slice(0, 10)
    },

    /** 一覧に出す期間。**空を「無期限」と書き分ける。** */
    periodLabel (start, end) {
      if (!start && !end) return '期限なし'
      if (start && !end) return start + ' 〜（期限なし）'
      if (!start && end) return '〜 ' + end
      return start + ' 〜 ' + end
    },

    /**
     * **テンプレートから使うので methods に置く。**
     * import しただけの関数は、テンプレートからは見えない。
     */
    selectedValue,

    /**
     * 一時パスの入口が開いているかを、画面を開いた時点で読む。
     *
     * <p><b>ここで読まないと、永久に出ない。</b>前は発行の直前に
     * しか読んでいなかったが、その発行ボタンは入口の中にある。
     * サーバを true にしても、運営は一度も一時パスを出せなかった。</p>
     *
     * <p><b>読めなかったら出さない。</b>false でも、enabled が
     * 無くても、403 でも、通信に失敗しても同じ。出して押させて
     * 断られるより、出ないほうがよい。</p>
     */
    /**
     * 一覧から選ぶ。**生の数字は受け取らない。**
     *
     * <p>選択肢に無い値が来たら選ばない。空を選んだら外す。
     * <b>ここを通らずに issueUserId が入る道を残さない。</b></p>
     */
    selectTemporaryPassUser (value) {
      const chosen = this.normalizedTemporaryPassUsers.find(
        user => String(user.id) === String(value))
      this.issueUserId = chosen ? chosen.id : null
    },

    /**
     * この会社の利用者を読む。
     *
     * <p><b>会社を切り替えたら、前の会社の選択を必ず消す。</b>会社Aで
     * 選んだあと会社Bへ切り替えて、古い番号をそのまま送る事故を防ぐ。
     * サーバは会社が合わなければ断るが、<b>そこで初めて気づく形に
     * しない。</b></p>
     *
     * <p>読めなかったら空にする。空なら選べないので、出せない。</p>
     */
    async loadTemporaryPassUsers () {
      this.temporaryPassUsers = []
      this.issueUserId = null
      this.issueTarget = null

      if (!this.selectedAccount) { return }

      try {
        const users = await api.getUsers(this.selectedAccount.id)
        this.temporaryPassUsers = Array.isArray(users) ? users : []
      } catch (error) {
        this.temporaryPassUsers = []
      }
    },

    async loadTemporaryPassStatus () {
      try {
        const status = await api.getTemporaryPassStatus()
        this.temporaryPassEnabled = status
          ? status.enabled === true
          : false
      } catch (error) {
        this.temporaryPassEnabled = false
      }
    },

    value (item, ...keys) {
      if (!item) return null
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(item, key) && item[key] !== undefined && item[key] !== null) return item[key]
        const upper = key.toUpperCase()
        if (Object.prototype.hasOwnProperty.call(item, upper) && item[upper] !== undefined && item[upper] !== null) return item[upper]
      }
      return null
    },
    targetTypeLabel (type) {
      return {
        ACCOUNT: '契約企業', CONTRACT: '契約情報', FEATURE: '利用可能機能',
        ROLE: 'ロール', USER: '利用者', USER_INVITATION: '管理者招待'
      }[type] || '設定情報'
    },
    invitationStatusLabel (status, emailSent) {
      if (status === 'PENDING' && !emailSent) return 'メール未送信'
      return INVITATION_STATUS_LABELS[status] || '状態不明'
    },
    invitationStatusColor (status) {
      return {
        PENDING: 'warning', ACCEPTED: 'success', EXPIRED: 'secondary',
        REVOKED: 'danger', EMAIL_SEND_FAILED: 'danger'
      }[status] || 'secondary'
    },
    formatDateTime (value) {
      if (!value) return '未記録'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      }).format(date)
    },
    async initialize () {
      if (this.initialized) return
      this.initialized = true
      try {
        const context = await api.getAccessContext()
        const roles = context && (context.roles || context.ROLES)
        if (!Array.isArray(roles) || !roles.includes('PLATFORM_SUPER_ADMIN')) {
          return this.$router.replace('/dashboard')
        }
        await Promise.all([this.loadAccounts(), this.loadPlans()])
      } catch (error) {
        this.handleError(error, '契約企業管理の初期表示に失敗しました。')
      }
    },
    async loadAccounts () {
      this.loading = true
      this.errorMessage = ''
      try {
        const result = await api.getAccounts()
        if (!Array.isArray(result)) throw new Error('契約企業一覧APIの応答形式が不正です。')
        this.accounts = result
      } catch (error) {
        this.accounts = []
        this.handleError(error, '契約企業一覧を取得できませんでした。')
        throw error
      } finally {
        this.loading = false
      }
    },
    async loadPlans () {
      const result = await api.getPlans()
      if (!Array.isArray(result)) throw new Error('プラン一覧APIの応答形式が不正です。')
      this.plans = result
    },
    openCreate () {
      this.createForm = emptyCreateForm()
      /* **前回断られた文言を残さない。**まだ何もしていないのに赤い字が
         出ている画面から始まってしまう。 */
      this.createError = ''
      this.createModal = true
    },
    /**
     * 足りないまま送らないための最後の砦。
     *
     * <p>見るのは <code>missingFields</code> ひとつだけ。<b>ここに条件を
     * もう1組書くと、画面の一覧と食い違う。</b></p>
     *
     * <p>画面ではボタンが押せないので、ここへ来るのは画面を通らない
     * 呼び出しだけ。足りない項目は幕の中の一覧に、押す前から出ている。</p>
     */
    validateCreate () {
      if (!this.missingFields.length) { return }
      throw new Error(this.missingFields.join('、') + 'を入力してください。')
    },

    /**
     * サーバへ送る形を作る。
     *
     * <p><b>連絡先は、未入力なら会社メールで埋める。</b>契約タブに連絡先の
     * 入力欄は無く、契約の更新はプラン・契約状態・利用人数上限しか
     * 受け取らない。つまり<b>ここで入れそこねると、あとから画面では
     * 直せない。</b>空で作るより、届く先が1つでも残っているほうがよい。</p>
     */
    createPayload () {
      const form = this.createForm
      const companyEmail = String(form.companyEmail || '').trim()
      const orCompanyEmail = value =>
        String(value || '').trim() || companyEmail
      return {
        ...form,
        contractContactEmail: orCompanyEmail(form.contractContactEmail),
        billingContactEmail: orCompanyEmail(form.billingContactEmail),
        securityContactEmail: orCompanyEmail(form.securityContactEmail)
      }
    },
    async createAccount () {
      this.saving = true
      this.errorMessage = ''
      this.createError = ''
      try {
        this.validateCreate()
        const result = await api.createAccount(this.createPayload())
        this.createModal = false
        this.successMessage = (result && result.message) || '契約企業を作成し、最初の契約企業管理者へ招待メールを送信しました。'
        await this.loadAccounts()
      } catch (error) {
        /*
         * **幕の中へ書く。**断られても幕は開いたまま——打った内容を
         * 捨てないため——なので、画面上端へ書くと暗幕の下で読めない。
         * 運営から見ると押しても画面が1ドットも変わらず、**押せなかった
         * ときと見分けが付かない。**
         */
        this.createError = this.failureText(
          error, '契約企業を作成できませんでした。')
      } finally {
        this.saving = false
      }
    },
    /**
     * 出す前の確認を開く（場面 S-95）。
     *
     * <p><b>開く前にサーバへ聞く。</b>いま生きている1本があるか、
     * 社内に救える別の人が居るか、入口が開いているか。どれも
     * <b>サーバが決める</b>ので、画面で数えない。</p>
     *
     * <p>聞けなかったら開かない。分からないまま出すと、読み上げ中の
     * 1本を黙って殺すかもしれない。</p>
     */
    async startIssueTemporaryPass () {
      if (!this.selectedAccount || !this.issueUserId) { return }

      /*
       * **一覧に居る人だけ。**選択肢を通らずに値が入っていたら進まない。
       * 番号を直接書き換えられても、ここで止まる。
       */
      const chosen = this.normalizedTemporaryPassUsers.find(
        user => Number(user.id) === Number(this.issueUserId))
      if (!chosen) { return }
      this.issueReason = ''
      this.issueLifetimeMinutes = DEFAULT_LIFETIME_MINUTES
      this.issuedPass = ''
      this.issuedExpiresAt = ''

      try {
        const state = await api.getTemporaryPassState(
          this.selectedAccount.id, this.issueUserId)
        this.livePassId = state.livePassId === undefined
          ? null
          : state.livePassId
        this.alternativeRecoveryAvailable =
          Boolean(state.alternativeRecoveryAvailable)

        /*
         * **開閉はここで決めない。**入口を出すかどうかは、画面を
         * 開いた時点の status で決まっている。ここで決めると、その
         * 入口を押さないと値が入らない——入口が出ないと押せないので、
         * 輪になる。
         *
         * 開いたまま閉じられた場合だけ、ここで消す。
         */
        if (state.enabled === false) {
          this.temporaryPassEnabled = false
          this.issueTarget = null
          return
        }

        this.issueTarget = this.issueUserId
      } catch (error) {
        /*
         * **黙って戻らない。**前はここで確認を開かないだけだった。
         * 運営から見ると、押しても画面が1ドットも変わらない——電話口で
         * 本人を待たせたまま、押せていないのか壊れているのかが分からない。
         */
        this.issueTarget = null
        this.handleError(error, '一時パスを出す準備ができませんでした。')
      }
    },

    /**
     * 使える時間を選ぶ。
     *
     * <p><b>選択肢の外は受け取らない。</b>部品は文字列で返すので数へ直す。
     * 直さずに送ると "30" のまま届き、受ける側の型次第で断られる。</p>
     */
    selectLifetimeMinutes (value) {
      const minutes = Number(value)
      this.issueLifetimeMinutes = LIFETIME_MINUTE_CHOICES.indexOf(minutes) >= 0
        ? minutes
        : DEFAULT_LIFETIME_MINUTES
    },

    /** 出すのをやめる。**打ちかけを残さない。** */
    cancelIssueTemporaryPass () {
      this.issueTarget = null
      this.issueReason = ''
      this.livePassId = null
      this.alternativeRecoveryAvailable = false
    },

    /**
     * 出す（場面 S-95）。
     *
     * <p><b>出す前に読んだ id を、そのまま送る。</b>送らないと、2人の
     * 運営が同時に出したときに、あとから通ったほうが先に渡した1本を
     * 黙って殺す。</p>
     */
    async confirmIssueTemporaryPass () {
      const target = this.issueTarget
      if (!target || !this.selectedAccount) { return }
      /*
       * 見るのは issueMissingFields ひとつだけ。**ここに条件をもう1組
       * 書くと、幕の中の一覧と食い違う。**
       */
      if (this.issueMissingFields.length) { return }
      const reason = String(this.issueReason || '').trim()

      try {
        const issued = await api.issueTemporaryPass(
          this.selectedAccount.id, target, {
            reason: reason,
            lifetimeMinutes: this.issueLifetimeMinutes,
            expectedLivePassId: this.livePassId
          })
        this.issuedPass = issued.pass || ''
        this.issuedExpiresAt = issued.expiresAt || ''
        this.issueReason = ''
      } catch (error) {
        /*
         * **黙って閉じない。**前は確認を閉じるだけだったので、運営には
         * 出たのか失敗したのかが分からなかった。**電話の相手を待たせた
         * まま**、読み上げる文字列も、断られた理由も出てこない。
         *
         * 確認は閉じる。理由は画面のいちばん上に出るので、幕を開けたまま
         * だと裏に隠れて読めない。
         */
        this.issueTarget = null
        this.handleError(error, '一時パスを出せませんでした。')
      }
    },

    /**
     * 出した文字列を閉じる。
     *
     * <p><b>持ち物からも消す。</b>画面から消しただけだと、開発者の
     * 道具から見えたままになる。</p>
     */
    closeIssuedPass () {
      this.issuedPass = ''
      this.issuedExpiresAt = ''
      this.issueTarget = null
      this.livePassId = null
      this.alternativeRecoveryAvailable = false
    },

    async selectAccount (item) {
      this.selectedAccount = item
      this.contractForm = {
        accountId: item.id,
        planId: item.planId,
        contractStatus: item.contractStatus || 'ACTIVE',
        userLimit: item.userLimit,
        contractStartDate: item.contractStartDate,
        contractEndDate: item.contractEndDate
      }
      this.ownerInvitation = null
      try {
        const [features, auditLogs] = await Promise.all([
          api.getFeatures(item.id),
          api.getAuditLogs(item.id)
        ])
        this.features = Array.isArray(features) ? features : []
        this.auditLogs = Array.isArray(auditLogs) ? auditLogs : []
        await this.loadOwnerInvitation()
        await this.loadTemporaryPassUsers()
      } catch (error) {
        this.handleError(error, '契約企業の詳細を取得できませんでした。')
      }
    },
    async loadOwnerInvitation () {
      if (!this.selectedAccount) return
      this.invitationLoading = true
      try {
        this.ownerInvitation = await api.getOwnerInvitation(this.selectedAccount.id)
      } catch (error) {
        this.ownerInvitation = null
        this.handleError(error, '最初の契約企業管理者の招待状態を取得できませんでした。')
      } finally {
        this.invitationLoading = false
      }
    },
    async resendInvitation () {
      if (!this.selectedAccount) return
      this.saving = true
      try {
        this.ownerInvitation = await api.resendOwnerInvitation(this.selectedAccount.id)
        this.successMessage = '招待メールを再送しました。'
      } catch (error) {
        this.handleError(error, '招待メールを再送できませんでした。')
      } finally {
        this.saving = false
      }
    },
    openReplaceInvitation () {
      this.replaceInvitationForm = {
        ownerName: this.ownerInvitation && this.ownerInvitation.invitedName ? this.ownerInvitation.invitedName : '',
        ownerEmail: '',
        ownerEmployeeId: ''
      }
      /* **前回断られた文言を残さない。**開いた瞬間に赤い字が出ていると、
         いま断られたのだと読める。 */
      this.replaceError = ''
      this.replaceInvitationModal = true
    },
    async replaceInvitation () {
      if (!this.selectedAccount) return
      /*
       * 見るのは replaceMissingFields ひとつだけ。**ここに条件をもう1組
       * 書くと、幕の中の一覧と食い違う。**画面ではボタンが押せないので、
       * ここへ来るのは画面を通らない呼び出しだけ。足りない項目は、押す
       * 前から幕の中に出ている。
       */
      if (this.replaceMissingFields.length) { return }
      this.saving = true
      this.replaceError = ''
      try {
        this.ownerInvitation = await api.replaceOwnerInvitation(this.selectedAccount.id, this.replaceInvitationForm)
        this.replaceInvitationModal = false
        this.successMessage = '招待先を変更し、新しい招待メールを送信しました。'
      } catch (error) {
        /*
         * **幕の中へ書く。**断られても幕は開いたままなので、画面上端では
         * 暗幕の下になる。ここは<b>いま使えている招待リンクを捨てる</b>
         * 操作——通ったのか断られたのかが分からないまま放置されると、
         * 新しい担当者にも古い担当者にも届かない状態を疑えない。
         */
        this.replaceError = this.failureText(
          error, '招待先を変更できませんでした。')
      } finally {
        this.saving = false
      }
    },
    async revokeInvitation () {
      if (!this.selectedAccount) return
      if (!window.confirm('現在の招待リンクを無効にします。よろしいですか？')) return
      this.saving = true
      try {
        this.ownerInvitation = await api.revokeOwnerInvitation(this.selectedAccount.id)
        this.successMessage = '招待を取消しました。'
      } catch (error) {
        this.handleError(error, '招待を取消できませんでした。')
      } finally {
        this.saving = false
      }
    },
    async saveContract () {
      this.saving = true
      try {
        await api.updateContract(this.contractForm)
        this.successMessage = '契約情報を更新しました。'
        await this.loadAccounts()
      } catch (error) {
        this.handleError(error, '契約情報を更新できませんでした。')
      } finally {
        this.saving = false
      }
    },
    async changeFeature (item, enabled) {
      try {
        await api.updateFeature({
          accountId: this.selectedAccount.id,
          featureCode: item.featureCode,
          enabled
        })
        this.features = await api.getFeatures(this.selectedAccount.id)
        this.successMessage = `${item.featureName}を${enabled ? '利用可能' : '利用不可'}にしました。`
      } catch (error) {
        this.handleError(error, '利用可能機能を更新できませんでした。')
      }
    },
    /**
     * 断られた理由の文言を作る。
     *
     * <p><b>出す先は呼ぶ側が決める。</b>幕を開いたままにする操作は
     * 画面上端へ書いても読まれない（{@link data} の createError を見よ）。
     * 文言の作り方だけをここに置き、<b>サーバの言い分をどこへ出しても
     * 同じ形になる</b>ようにする。</p>
     */
    failureText (error, fallback) {
      const data = error && error.response && error.response.data
      const status = error && error.response && error.response.status
      const detail = data && (data.message)
      // ステータス番号は画面へ出さず、調査用にコンソールへ残す。
      if (status) console.error(`契約管理の操作に失敗しました (${status})`, safeErrorDigest(error))
      return `${fallback}${detail ? ` ${detail}` : ''}`
    },
    handleError (error, fallback) {
      this.errorMessage = this.failureText(error, fallback)
    }
  }
}
</script>

<style scoped>
.platform-access-page { min-width: 0; padding: 24px; }
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 20px; }
.page-header__copy { min-width: 0; }
.page-header h1 { margin: 0 0 8px; font-weight: 700; }
.page-header p { max-width: 820px; margin-bottom: 0; overflow-wrap: anywhere; }
.page-header__action, .primary-action, .mobile-primary-action { min-height: 42px; white-space: normal; }
.eyebrow { margin: 0 0 4px; color: #60738a; font-size: 12px; font-weight: 700; letter-spacing: .16em; }
.provider-scope-alert { line-height: 1.7; }
.access-card { min-width: 0; margin-bottom: 18px; }
.section-header, .selected-account-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; overflow-wrap: anywhere; }
.selected-account-header > div { display: grid; gap: 4px; }
.selected-account-header small { color: #657987; font-weight: 400; }
.responsive-table-shell { width: 100%; min-width: 0; overflow-x: auto; -webkit-overflow-scrolling: touch; }
.responsive-table-shell::v-deep table { min-width: 900px; }
.action-cell, .switch-cell { white-space: nowrap; }
/*
  表とカードは同じ中身で、画面幅に応じて片方だけを出す。

  切り替えは .mobile-only / .desktop-only が持っている。ところが同じ div に
  付けた一覧クラスにも display があり、後ろに書いた方が勝つため、広い画面
  でも表の下にカードが並んでいた。組み合わせで明示して、書き順に左右され
  ないようにする。
*/
.mobile-only { display: none; }
.mobile-only.mobile-record-list { display: none; }
.mobile-record-list { display: grid; gap: 12px; }
.mobile-record-card { min-width: 0; padding: 14px; border: 1px solid #d8e4ec; border-radius: 12px; background: #fff; box-shadow: 0 6px 18px rgba(28, 68, 91, .06); }
.mobile-record-card__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
.mobile-record-card__header strong { min-width: 0; color: #17384d; font-size: 15px; line-height: 1.45; overflow-wrap: anywhere; }
.mobile-record-card__header .badge { flex: 0 0 auto; white-space: nowrap; }
.mobile-record-card__date { flex: 0 0 auto; color: #64748b; font-size: 11px; line-height: 1.4; text-align: right; white-space: normal; }
.mobile-detail-list, .invitation-detail-list { display: grid; gap: 8px; margin: 0 0 14px; }
.mobile-detail-list > div, .invitation-detail-list > div { display: grid; grid-template-columns: minmax(110px, 40%) minmax(0, 1fr); gap: 10px; min-width: 0; }
.mobile-detail-list dt, .invitation-detail-list dt { margin: 0; color: #657987; font-size: 12px; font-weight: 700; }
.mobile-detail-list dd, .invitation-detail-list dd { min-width: 0; margin: 0; color: #17384d; text-align: right; overflow-wrap: anywhere; }
.mobile-primary-action { width: 100%; }
.mobile-switch-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-top: 10px; border-top: 1px solid #e6edf2; font-weight: 700; }
.mobile-empty-state { padding: 28px 14px; border: 1px dashed #cbd8e1; border-radius: 12px; color: #687d8a; text-align: center; }
.invitation-card { padding: 20px; border: 1px solid #d8e5ec; border-radius: 15px; background: #fff; box-shadow: 0 8px 24px rgba(26, 65, 86, .07); }
.invitation-card__header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 18px; gap: 12px; }
.invitation-card__header p { margin: 0; color: #687d89; font-size: 10px; font-weight: 800; }
.invitation-card__header h3 { margin: 4px 0 0; color: #17394d; font-size: 20px; }
.invitation-message { margin: 14px 0; padding: 10px 12px; border-radius: 9px; background: #f5f8fa; color: #58707d; font-size: 12px; }
.invitation-actions { display: flex; flex-wrap: wrap; gap: 9px; }
.form-block-title { margin: 22px 0 12px; padding-bottom: 7px; border-bottom: 1px solid #e1e9ee; color: #2b4c5f; }
.form-note { margin: 8px 0 0; padding: 11px 13px; border-radius: 9px; background: #f3f7f9; color: #5b7380; font-size: 11px; line-height: 1.7; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; width: 100%; }
.platform-access-page::v-deep .access-tabs .nav-tabs { flex-wrap: nowrap; overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; }
.platform-access-page::v-deep .access-tabs .nav-link { white-space: nowrap; }

@media (max-width: 767.98px) {
  .platform-access-page { padding: 12px 0 92px; }
  .page-header { flex-direction: column; gap: 16px; margin-bottom: 16px; }
  .page-header h1 { font-size: 28px; line-height: 1.25; }
  .page-header__action, .primary-action, .section-header__action { width: 100%; min-height: 44px; }
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .mobile-only.mobile-record-list { display: grid; }
  .section-header { align-items: stretch; flex-direction: column; }
  .platform-access-page::v-deep .card-header { padding: 14px; }
  .platform-access-page::v-deep .card-body { padding: 12px; }
  .selected-account-header strong { font-size: 15px; line-height: 1.5; }
  .contract-form-row { margin-right: -6px; margin-left: -6px; }
  .contract-form-row > div { padding-right: 6px; padding-left: 6px; }
  .mobile-record-card__header, .invitation-card__header { flex-wrap: wrap; }
  .mobile-record-card__date { flex-basis: 100%; text-align: left; }
  .mobile-detail-list > div, .invitation-detail-list > div { grid-template-columns: minmax(0, 1fr); gap: 3px; padding: 8px 0; border-bottom: 1px solid #e6edf2; }
  .mobile-detail-list > div:last-child, .invitation-detail-list > div:last-child { border-bottom: 0; }
  .mobile-detail-list dd, .invitation-detail-list dd { text-align: left; }
  .invitation-actions { display: grid; grid-template-columns: minmax(0, 1fr); }
  .invitation-actions .btn { width: 100%; min-height: 44px; margin: 0; white-space: normal; }
  .modal-actions { flex-direction: column-reverse; gap: 8px; }
  .modal-actions .btn { width: 100%; min-height: 44px; margin: 0; white-space: normal; }
  .platform-access-page::v-deep .access-modal .modal-dialog { width: auto; max-width: calc(100vw - 16px); margin: 8px; }
  .platform-access-page::v-deep .access-modal .modal-body { max-height: calc(100vh - 190px); overflow-y: auto; }
  .platform-access-page::v-deep .access-modal .modal-footer { padding: 12px; }
}

@media (min-width: 768px) {
  .desktop-only { display: block; }
}
</style>
